/**
 * Company field for new-record (quick-create) forms on S / S? documents —
 * invoices, purchase orders, sales orders, journal entries.
 *
 * Implements the create-time company precedence from
 * docs/platform/multi-company-design.md §"Creating a new record — company
 * precedence":
 *
 *   1. Parent/context company wins — when created from a linked record the
 *      company is inherited and shown as a read-only "Creating in X" chip.
 *   2. Else the global scope, if a specific company — pre-filled and shown as a
 *      chip the user must ACKNOWLEDGE ("Creating in X · Confirm · Change")
 *      before the record can be created. Clicking Change reveals the picker;
 *      picking from it is an acknowledgement in its own right.
 *   3. Else (scope = All companies) — the required company picker is shown.
 *   4. Single-company tenant — nothing is rendered (the dimension is implicit).
 *
 * Rule 2's acknowledgement is the one part of this that is not a formality: a
 * scope-derived company is the ONLY one nobody chose for this record, and
 * filing a document against whatever the app bar happened to be set to is the
 * error the whole affordance exists to prevent (design doc §3, "Why rule 2
 * gained a confirmation"). Rules 1, 3 and 4 never prompt — a parent's company
 * was chosen by the record it came from, a picked company was chosen outright,
 * and a sole company has no alternative.
 *
 * **A confirmation is only ever outstanding while this field is SHOWING the
 * scope's company.** The field publishes that fact to the hook
 * (`confirmation.setOutstanding`), and the submit gates read it back. That
 * direction matters: a form that renders no company field (an edit, a copy of
 * an existing entry) or whose value came from somewhere other than the scope (a
 * resumed import batch, a bank feed's own company) must not be blocked by a
 * prompt with no control to answer it.
 *
 * Distinct from the on-record `DocumentCompanyField` (which edits an existing
 * draft's company) and from the app-bar `CompanyScopeSwitcher` (the global
 * filter). This is the creation-time affordance only.
 *
 * Controlled: the parent owns `value` (companies.id, '' = none) and the submit
 * payload. It also owns the ONE `useCompanyCreatePrecedence` call for the form
 * — the precedence object seeds the initial value (`defaultCompanyId`), gates
 * the create (`companyBlockReason` / `blockCreate`) and is handed to this
 * component as `precedence`, so the acknowledgement has a single owner.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Check, Pencil } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { RequiredMarker } from '../../../components/ui/label';
import { useCompanyScope } from '../../contexts/CompanyScopeContext';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';
import { CompanyPicker } from '../common/CompanyPicker';

export interface CompanyCreatePrecedence {
  /** Accessible companies for the current user. */
  companies: Array<{ id: string; name: string }>;
  /** True when the tenant exposes ≥2 companies (the dimension is visible). */
  multiCompany: boolean;
  /**
   * The company id to pre-fill a new record with: the context/parent company
   * if any, else the specific global scope, else '' (must pick / single-co).
   */
  defaultCompanyId: string;
  /** Scope = "All companies" and no context → the picker is required. */
  requiresPick: boolean;
  /** A parent/context company was supplied — it wins and is read-only. */
  contextLocked: boolean;
  /** The reason a blocked create was refused, once `blockCreate` revealed it. */
  blockedMessage: string | null;
  /**
   * PURE gate — the translated reason this company may not be created with, or
   * `null` when it may. Render-safe, and the accessor to read if you need the
   * reason itself. **`blockCreate` is what a form should call**; this one has
   * two homes it must not be given:
   *
   * - a `DialogForm isDisabled` or any other render-time "is the company
   *   resolved" gate. Those already answer "is there a company at all"; adding
   *   the acknowledgement there greys the submit button out instead of
   *   prompting, and the user gets a dead Create with nothing said.
   * - a react-hook-form `validate`. RHF caches a field's error until that
   *   FIELD re-validates, and confirming the chip changes no value — so the
   *   "confirm the company" message would sit under a chip that no longer asks
   *   for one, until the next submit. Put `blockCreate` in the submit handler
   *   and let the field paint its own message, which `confirm()` clears.
   */
  companyBlockReason: (companyId: string) => string | null;
  /**
   * IMPURE gate for an imperative submit/finalize handler — `true` when the
   * create must be stopped, and it reveals the reason inline on the field
   * (which then takes focus). Never call this during render.
   */
  blockCreate: (companyId: string) => boolean;
  /** Wiring for `CompanyCreateField`; not for call sites. */
  confirmation: {
    /** The global scope pre-filled this create and nobody has acknowledged it. */
    pending: boolean;
    /** The company id the global scope contributed ('' when it contributed none). */
    scopeCompanyId: string;
    /** Record the user's acknowledgement. */
    confirm: () => void;
    /** Drop a previous acknowledgement, so the NEXT create asks again. */
    reset: () => void;
    /** Publish that a field is on screen to carry a refusal at all. */
    setMounted: (mounted: boolean) => void;
    /** Publish whether an acknowledgement is actually outstanding on screen. */
    setOutstanding: (outstanding: boolean) => void;
  };
}

/**
 * Resolve the create-time company precedence for a quick-create form, and own
 * the acknowledgement state rule 2 needs.
 *
 * Call it ONCE per form, in the form itself: `defaultCompanyId` seeds the form
 * value, the gates block the create, and the whole object goes to
 * `CompanyCreateField` as `precedence`. Calling it a second time (as the field
 * used to, internally) gives the field its own acknowledgement state, which the
 * submit gate cannot see.
 */
export function useCompanyCreatePrecedence(
  contextCompanyId?: string | null,
): CompanyCreatePrecedence {
  const { t } = useTranslation('common');
  const { data } = useMyCompanies();
  const { companyScopeId } = useCompanyScope();
  // `?? []` is a fresh array whenever the query has not answered, which would
  // re-create the memoized result below on every render.
  const companies = useMemo(() => data?.data ?? [], [data]);
  const multiCompany = companies.length > 1;

  const context = contextCompanyId ?? '';
  const contextLocked = !!context;
  // Specific scope (not "All") pre-fills; context overrides scope entirely.
  const scopeDefault = !contextLocked && companyScopeId ? companyScopeId : '';
  // Single-company tenant: the sole company is implicit (precedence step 4 —
  // "always auto, never shown"). Seed it so the create payload carries a
  // company_id even under an "All companies" scope; otherwise the backend
  // rejects the quick-create with 400 "company_id is required".
  const soleCompanyId = companies.length === 1 ? companies[0].id : '';
  const defaultCompanyId = context || scopeDefault || soleCompanyId;

  const [acknowledged, setAcknowledged] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [outstanding, setOutstanding] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);

  // A different company arrived (the user moved the app-bar scope, or a parent
  // record resolved) — whatever they acknowledged, it was not this one.
  useEffect(() => {
    setAcknowledged(false);
    setBlockedMessage(null);
  }, [defaultCompanyId]);

  const confirm = useCallback(() => {
    setAcknowledged(true);
    setBlockedMessage(null);
  }, []);

  const resetConfirmation = useCallback(() => {
    setAcknowledged(false);
    setBlockedMessage(null);
  }, []);

  // Identical state bails out of the re-render, so the field may publish on
  // every render without looping.
  const publishMounted = useCallback((next: boolean) => {
    setMounted((prev) => (prev === next ? prev : next));
  }, []);

  const publishOutstanding = useCallback((next: boolean) => {
    setOutstanding((prev) => (prev === next ? prev : next));
  }, []);

  const companyBlockReason = useCallback(
    (companyId: string): string | null => {
      if (!multiCompany) return null;
      // NOTHING IS REFUSED WHILE NO FIELD IS ON SCREEN. A refusal the user
      // cannot see and cannot answer is a Save that silently does nothing, and
      // this hook is called by forms that render the field CONDITIONALLY — an
      // edit, a repost/copy, a page form whose edit arm uses a plain picker, or
      // simply the first render before `useMyCompanies` has answered. Each of
      // those was, or could become, a dead Save button; making the gate
      // structurally inert without a field is what stops the next one, rather
      // than another `!isEditing &&` at the call site that somebody forgets.
      if (!mounted) return null;
      if (!companyId) return t('validation.required');
      if (outstanding) return t('multiCompany.confirmPrompt');
      return null;
    },
    [mounted, multiCompany, outstanding, t],
  );

  const blockCreate = useCallback(
    (companyId: string): boolean => {
      const reason = companyBlockReason(companyId);
      setBlockedMessage(reason);
      return reason !== null;
    },
    [companyBlockReason],
  );

  // Memoized: two call sites hold this object in a `useCallback` dependency
  // array (the import pages' finalize handlers), and a fresh literal per render
  // would defeat those entirely.
  return useMemo(
    () => ({
      companies,
      multiCompany,
      defaultCompanyId,
      requiresPick: multiCompany && !defaultCompanyId,
      contextLocked,
      blockedMessage,
      companyBlockReason,
      blockCreate,
      confirmation: {
        // Only the SCOPE-derived company is silent, and only in a tenant that
        // has an alternative to file it under.
        pending: multiCompany && !contextLocked && !!scopeDefault && !acknowledged,
        scopeCompanyId: scopeDefault,
        confirm,
        reset: resetConfirmation,
        setMounted: publishMounted,
        setOutstanding: publishOutstanding,
      },
    }),
    [
      acknowledged,
      blockCreate,
      blockedMessage,
      companies,
      companyBlockReason,
      confirm,
      contextLocked,
      defaultCompanyId,
      multiCompany,
      publishMounted,
      publishOutstanding,
      resetConfirmation,
      scopeDefault,
    ],
  );
}

export interface CompanyCreateFieldProps {
  /**
   * The form's single `useCompanyCreatePrecedence(...)` result. Required: it
   * carries the acknowledgement the submit gate also reads, so the field must
   * not resolve its own.
   */
  precedence: CompanyCreatePrecedence;
  /** Selected company id; '' = none (submit blocks while a pick is required). */
  value: string;
  onChange: (companyId: string) => void;
  /** Field label override; defaults to the tenant `{{Company}}` term. */
  label?: string;
  /** Show the picker in an error state (only meaningful in picker mode). */
  error?: boolean;
  /** Error message rendered under the picker when {@link error} is true. */
  errorMessage?: string;
  /**
   * DOM id for the picker's trigger. Also associates the rendered `<Label>`
   * with the control (`htmlFor`) — without it the picker has no programmatic
   * label — and gives tests a stable hook for a required selling-company pick.
   */
  id?: string;
  className?: string;
}

export function CompanyCreateField({
  precedence,
  value,
  onChange,
  label,
  error,
  errorMessage,
  id,
  className,
}: CompanyCreateFieldProps) {
  const { t } = useTranslation('common');
  const errorId = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const {
    companies,
    multiCompany,
    requiresPick,
    contextLocked,
    blockedMessage,
    confirmation,
  } = precedence;
  const { pending, scopeCompanyId, confirm, reset, setMounted, setOutstanding } = confirmation;

  // While the chip is shown the user may click "Change" to reveal the picker.
  const [changing, setChanging] = useState(false);
  // Reset the local toggle whenever the resolved company changes (dialog open
  // seeds a new value) so a reused dialog never reopens stuck in picker mode.
  useEffect(() => {
    setChanging(false);
  }, [value, contextLocked]);

  const selected = companies.find((c) => c.id === value) ?? null;

  // An acknowledgement is outstanding while this field is showing the SCOPE's
  // company and nobody has answered for it. The conjuncts are the cases that
  // must not prompt: a value that is not the scope's came from a record rather
  // than the app bar, and a scope naming a company the user can no longer access
  // resolves to "—", which is nothing to confirm (the app-bar switcher self-heals
  // it to "All companies").
  //
  // `changing` is deliberately NOT a conjunct. Revealing the picker is not an
  // answer — clicking Change and then Create without picking anything would
  // otherwise file the untouched scope company, which is the exact bypass this
  // field exists to close. The picker carries the refusal instead of the chip.
  const outstanding = pending && value === scopeCompanyId && !!selected;

  // Publish that a field is on screen at all — the gate is inert without one.
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, [setMounted]);

  useEffect(() => {
    setOutstanding(outstanding);
    return () => setOutstanding(false);
  }, [outstanding, setOutstanding]);

  // Every fresh create asks again. Radix unmounts a closed dialog's content, so
  // a mount of this field IS a dialog opening — while the FORM around it, and
  // therefore `useCompanyCreatePrecedence`'s state, survives the close. Without
  // this the second order of a session inherits the first one's acknowledgement,
  // which is a run of orders filed silently: exactly what this field prevents.
  useEffect(() => {
    reset();
    // Mount only — `reset` is stable, and re-running it on any later render
    // would undo the acknowledgement the user just gave.
  }, [reset]);

  const resolvedError = error || !!blockedMessage;
  const resolvedErrorMessage = errorMessage ?? blockedMessage ?? undefined;

  // A blocked create must land the caret on what blocked it. `DialogForm`'s own
  // error watcher only focuses `input`/`select`/`[role=combobox]`, and five of
  // these surfaces are pages with no watcher at all, so the chip does it itself.
  useEffect(() => {
    if (resolvedError && outstanding) confirmRef.current?.focus();
  }, [resolvedError, outstanding]);

  // Single-company tenant: the dimension is implicit, render nothing.
  if (!multiCompany) return null;

  const resolvedLabel = label ?? t('multiCompany.company');

  // Step 3 / after "Change": the required picker (All-companies scope).
  if (requiresPick || changing) {
    return (
      <CompanyPicker
        value={value || null}
        onChange={(companyId) => {
          // An explicit pick IS the acknowledgement — rule 2 only distrusts a
          // company nobody chose.
          onChange(companyId ?? '');
          if (companyId) confirm();
        }}
        label={resolvedLabel}
        required
        error={resolvedError}
        errorMessage={resolvedErrorMessage}
        id={id}
        className={className}
      />
    );
  }

  // Steps 1–2: a chip showing the inherited/pre-filled company.
  const companyName = selected?.name ?? '—';
  const creatingInLabel = t('multiCompany.creatingIn');
  return (
    <div className={cn('space-y-1', className)}>
      <div
        role="group"
        aria-label={`${creatingInLabel} ${companyName}`}
        className={cn(
          'inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-muted/40 px-2.5 py-1.5 text-sm',
          resolvedError ? 'border-destructive' : 'border-border',
        )}
      >
        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 truncate">
          <span className="text-muted-foreground">{creatingInLabel} </span>
          <span className="font-medium text-foreground">{companyName}</span>
          {outstanding && <RequiredMarker />}
        </span>
        {outstanding && (
          <Button
            ref={confirmRef}
            type="button"
            size="sm"
            onClick={confirm}
            data-testid="company-create-confirm"
            aria-describedby={resolvedError && resolvedErrorMessage ? errorId : undefined}
            className="h-7 shrink-0 px-2.5"
          >
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            {t('multiCompany.confirmCompany')}
          </Button>
        )}
        {!contextLocked && (
          <>
            {!outstanding && (
              <span className="text-muted-foreground" aria-hidden="true">
                ·
              </span>
            )}
            {/* Inline text affordance; -my-1.5 widens the tap area (~44px) to the
                chip edges without growing the chip's visual height. */}
            <button
              type="button"
              onClick={() => setChanging(true)}
              className="-my-1.5 inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-2 font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Pencil className="h-3 w-3" aria-hidden="true" />
              {t('multiCompany.change')}
            </button>
          </>
        )}
      </div>
      {resolvedError && resolvedErrorMessage && (
        <p id={errorId} data-testid="company-create-error" className="text-sm text-destructive">
          {resolvedErrorMessage}
        </p>
      )}
    </div>
  );
}
